import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "@/components/ui/use-toast"
import { useAsyncEffect } from "@/utils"
import { useState } from "preact/hooks"
import { api_fetch } from "../api/client.tsx"
import { Loader2 } from "lucide-react"

type XeroCreditMemoLineMode = "grouped_by_gl" | "per_deduction"

interface XeroCreditMemoLineModeSetting {
  xero_credit_memo_line_mode: XeroCreditMemoLineMode
}

export function XeroCreditMemoLineModeSettings() {
  const [setting, setSetting] = useState<XeroCreditMemoLineModeSetting | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useAsyncEffect(async () => {
    setIsLoading(true)
    try {
      const res = await api_fetch<XeroCreditMemoLineModeSetting>("/organization/xero-credit-memo-line-mode")
      if (res.ok) {
        setSetting(res.value.data)
      } else {
        toast({ title: "Error", description: "Failed to load credit memo settings", variant: "destructive" })
      }
    } catch (error) {
      console.error("Failed to fetch credit memo line mode settings:", error)
      toast({ title: "Error", description: "Failed to load credit memo settings", variant: "destructive" })
    } finally {
      setIsLoading(false)
    }
  }, [])

  async function handleSave() {
    if (!setting) return
    setIsSaving(true)
    try {
      const res = await api_fetch<XeroCreditMemoLineModeSetting>("/organization/xero-credit-memo-line-mode", {
        method: "POST",
        body: setting,
      })
      if (res.ok) {
        toast({ title: "Settings Updated", description: "Credit memo line grouping saved." })
        setSetting(res.value.data)
      } else {
        toast({ title: "Error", description: res.error?.message || "Failed to update settings", variant: "destructive" })
      }
    } catch (error) {
      console.error("Failed to update credit memo line mode settings:", error)
      toast({ title: "Error", description: "Failed to update settings. Please try again.", variant: "destructive" })
    } finally {
      setIsSaving(false)
    }
  }

  function handleChange(mode: XeroCreditMemoLineMode) {
    if (!setting) return
    setSetting({ ...setting, xero_credit_memo_line_mode: mode })
  }

  if (isLoading) {
    return <div class="text-gray-500">Loading credit memo settings...</div>
  }

  if (!setting) {
    return <div class="text-gray-500">Failed to load settings</div>
  }

  return (
    <div class="bg-white rounded-lg shadow-sm p-6 space-y-6">
      <h2 class="text-lg font-semibold leading-7 text-gray-900">Credit memo line grouping</h2>
      <div class="space-y-3 max-w-md">
        <Label htmlFor="xero-line-mode" class="cursor-pointer">
          How deductions become Xero credit notes
        </Label>
        <Select value={setting.xero_credit_memo_line_mode} onValueChange={(v: string) => handleChange(v as XeroCreditMemoLineMode)}>
          <SelectTrigger id="xero-line-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="per_deduction">One credit note per deduction</SelectItem>
            <SelectItem value="grouped_by_gl">Group by GL account</SelectItem>
          </SelectContent>
        </Select>
        <p class="text-sm text-gray-500">
          One credit note per deduction preserves each deduction's execution date and invoice reference
          individually. Recommended for distributor customers. Group by GL account rolls all deductions on a
          check up into a single credit note.
        </p>
      </div>

      <div class="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Settings"
          )}
        </Button>
      </div>
    </div>
  )
}
